import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Group, LoadingOverlay, Paper, Stack, Text, Title } from '@mantine/core';
import { useTabStatusContext } from '@/hooks';
import { Icon } from '../Icon';
import styles from './MinistryScheduler.module.sass';
import type { MinistrySchedulerView } from '@/types/ministryScheduler';
import { EditMinistrySchedulerModal } from '@/components';
import { useMinistryScheduler, useUpdateMinistrySchedulerMutation } from '@/hooks/organization-programs';

const LAYOUT = {
  ARROW_LEFT_ICON_SIZE: 16,
  LOADING_PAPER_MIN_HEIGHT: 200,
};

const MinistryScheduler = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { refreshTabStatus } = useTabStatusContext();
  const { data: ministrySchedulerData, isLoading, isError } = useMinistryScheduler(churchId);
  const updateMinistrySchedulerMutation = useUpdateMinistrySchedulerMutation(churchId ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  const handleEditMinistryScheduler = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditMinistryScheduler = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveMinistryScheduler = async (updatedData: MinistrySchedulerView) => {
    await updateMinistrySchedulerMutation.mutateAsync({
      isActive: updatedData.isActive,
    });
    setIsEditModalOpen(false);
    refreshTabStatus();
  };

  if (isLoading) {
    return (
      <Paper p="xl" radius="md" w="100%" pos="relative" style={{ minHeight: LAYOUT.LOADING_PAPER_MIN_HEIGHT }}>
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (isError || !ministrySchedulerData) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Failed to load ministry scheduler data</Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={3}>Ministry Scheduler</Title>
            <Button
              onClick={handleEditMinistryScheduler}
              variant="default"
              size="xs"
              leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
            >
              Edit
            </Button>
          </Group>

          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Status:
              </Text>
              <Badge color={ministrySchedulerData?.isActive ? 'green' : 'red'} variant="light">
                {ministrySchedulerData?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Group>
          </Group>
        </Stack>
      </Paper>

      <EditMinistrySchedulerModal
        opened={isEditModalOpen}
        ministrySchedulerData={ministrySchedulerData}
        onClose={handleCloseEditMinistryScheduler}
        onSave={handleSaveMinistryScheduler}
      />
    </>
  );
};

export default MinistryScheduler;
